import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { orderBy, reduce } from 'lodash';
import { getAdvertsSetup } from '../../store/page-info/selectors';

const AdvertContainer = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  ${(props) => {
    const { responsiveSizes, size } = props;

    if (!responsiveSizes) {
      return Array.isArray(size) && size.length === 2 && size[1] !== 1
        ? `min-height: ${size[1]}px;`
        : '';
    }

    const responsiveHeights = responsiveSizes.map((responsiveSize) => {
      const { breakpoint, sizes } = responsiveSize;
      if (!Array.isArray(sizes)) return null;

      const [breakpointMinWidth, breakpointMinHeight] = breakpoint;
      if (!Array.isArray(sizes[0])) {
        return {
          breakpointMinWidth,
          breakpointMinHeight,
          largestHeight: sizes[1],
        };
      }

      const sizeHeights = responsiveSize.sizes.map((sizeArray) =>
        Array.isArray(sizeArray) && sizeArray.length === 2 ? sizeArray[1] : 0,
      );

      return {
        breakpointMinWidth,
        breakpointMinHeight,
        largestHeight: Math.max(...sizeHeights),
      };
    });

    return reduce(
      orderBy(responsiveHeights, ['largestHeight'], ['asc']),
      (css, responsiveHeight) => {
        if (!responsiveHeight) return css;
        const { breakpointMinWidth, breakpointMinHeight, largestHeight } = responsiveHeight;
        return `
          ${css}
          @media screen and (min-width: ${breakpointMinWidth}px) and (min-height: ${breakpointMinHeight}px) {
            min-height: ${largestHeight}px;
          };
        `;
      },
      '',
    );
  }}
`;

const Advert = ({ adConfig, advertsSetup, className, style: customStyle, onError, onLoad }) => {
  const ref = useRef();
  const timeoutRef = useRef(null);
  const adHandledRef = useRef(false);

  useEffect(() => {
    if (!advertsSetup || !ref.current || !adConfig) return;

    const div = ref.current;

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    adHandledRef.current = false;

    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];

    window.googletag.cmd.push(() => {
      // Define the slot
      const slot = window.googletag.defineSlot(adConfig.path, adConfig.sizes, div.id);
      if (!slot) return;

      slot.addService(window.googletag.pubads());

      // Enable services if needed
      if (!window.googletag.servicesEnabled) {
        window.googletag.enableServices();
        window.googletag.servicesEnabled = true; // flag to prevent multiple calls
      }

      // Display the ad
      window.googletag.display(div.id);
    });

    // Observe DOM changes to detect ad content
    const observer = new MutationObserver(() => {
      if (!adHandledRef.current) {
        adHandledRef.current = true;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (onLoad) onLoad();
        observer.disconnect();
      }
    });

    observer.observe(div, { childList: true, subtree: true });

    // Timeout fallback in case the ad fails
    timeoutRef.current = setTimeout(() => {
      if (!adHandledRef.current) {
        if (onError) onError();
      }
    }, 4000);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      observer.disconnect();

      // Clear div content so GPT can render fresh next time
      if (div) div.innerHTML = '';
    };
  }, [advertsSetup, adConfig, onError, onLoad]);

  return (
    <AdvertContainer
      className={className}
      responsiveSizes={adConfig.responsiveSizes}
      size={adConfig.size}
      style={customStyle}
    >
      <div
        className="js-ad"
        id={adConfig.id} // use the GPT expected ID
        ref={ref}
        style={{ overflow: 'hidden', margin: '0 auto', maxWidth: '100%' }}
      />
    </AdvertContainer>
  );
};

Advert.propTypes = {
  adConfig: PropTypes.shape().isRequired,
  advertsSetup: PropTypes.bool.isRequired,
  className: PropTypes.string,
  style: PropTypes.shape(),
  onError: PropTypes.func,
  onLoad: PropTypes.func,
};

Advert.defaultProps = {
  className: null,
  style: {},
  onError: null,
  onLoad: null,
};

export default connect((state) => ({ advertsSetup: getAdvertsSetup(state) }))(Advert);
